import {
  composeRenderProps,
  Button as RACButton,
  ButtonProps as RACButtonProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

import { Rotate } from '@matrixreq/icons';

import { focusRing } from '../../utils';

export interface ButtonProps extends RACButtonProps {
  /**
   * Specifies the variant of the button. Each variant has a unique styling.
   */
  variant?:
    | 'primary'
    | 'secondary'
    | 'destructive'
    | 'destructive-secondary'
    | 'icon'
    | 'destructive-icon';

  /**
   * Element to be rendered in the left slot of the button. Typically an icon.
   */
  iconLeft?: React.ReactNode;

  /**
   * Element to be rendered in the right slot of the button. Typically an icon.
   */
  iconRight?: React.ReactNode;

  /**
   * If true, the button will take up the full width of its container.
   * @default false
   * */
  fullWidth?: boolean;
  /**
   * If true the loading spinner will be shown.
   * @default false
   * */
  isLoading?: boolean;
}

let button = tv({
  extend: focusRing,
  base: 'font-poppins flex cursor-default items-center gap-1 rounded-lg px-4 py-2 text-center text-sm shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)] transition dark:shadow-none',
  variants: {
    variant: {
      primary:
        'bg-main-primary hover:bg-main-hover pressed:bg-main-pressed pressed:text-main-primary text-white',
      secondary:
        'bg-main-secondary border-main-primary hover:bg-main-hover pressed:bg-main-pressed text-main-primary border hover:text-white',
      destructive:
        'bg-danger-main hover:bg-danger-hover pressed:bg-danger-pressed pressed:text-danger-main text-white',
      'destructive-secondary':
        'bg-main-secondary border-danger-main hover:bg-danger-hover pressed:bg-danger-pressed text-danger-main border hover:text-white',
      icon: 'bg-main-secondary hover:bg-main-hover pressed:bg-main-secondary flex items-center justify-center border-0 p-2 text-gray-100 hover:text-white disabled:bg-transparent',
      'destructive-icon':
        'bg-danger-main hover:bg-danger-hover pressed:bg-danger-pressed pressed:text-danger-main flex items-center justify-center border-0 p-2 text-white disabled:bg-transparent',
    },
    isDisabled: {
      true: 'bg-gray-20 hover:bg-gray-20 border-none text-gray-50 hover:text-gray-50 forced-colors:text-[GrayText]',
    },
    fullWidth: {
      true: 'w-full justify-center',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

const Button = ({
  iconLeft,
  iconRight,
  fullWidth = false,
  isLoading = false,
  variant,
  children,
  ...props
}: ButtonProps) => (
  <RACButton
    {...props}
    className={composeRenderProps(props.className, (className, renderProps) =>
      button({ ...renderProps, variant, fullWidth, className }),
    )}
  >
    <>
      {variant !== 'icon' && iconLeft && <span>{iconLeft}</span>}
      {children}
      {variant !== 'icon' && iconRight && !isLoading && (
        <span>{iconRight}</span>
      )}
      {isLoading && <Rotate className="animate-spin" />}
    </>
  </RACButton>
);

export { Button };
